
import img0 from './Images/Configure_Data_Analyzer.png';
  
const content = `# Deploy Data Analyzer

OnData SecureFile offers an optional Data Analyzer component that automatically identifies sensitive information in documents and files leveraging Artificial 
Intelligence (AI) technology. While the SecureFile clients can identify most of the sensitive Information Types in documents and files, the Data Analyzer component 
can augment SecureFile's capabilities in identifying sensitive Information Types and Document Types.  
  
If you choose to enable the Data Analyzer component, it is recommended that you deploy the Data Analyzer component in your own network environment, so that the 
documents and files to be analyzed are routed within your own network, without being sent to OnData for analysis. If you do not have a Data Analyzer component 
deployed and configured in your own environment, and you still would like to enable the Data Analyzer feature, the Managed Data Analyzer hosted by the OnData 
platform will be used. That means your sensitive data will be securely sent to the OnData platform for analysis. The OnData platform will discard the data received 
right after the analysis and won't store a copy of the data. You will be prompted to confirm that you are certain to proceed with that option and acknowledge the 
risk associated with sending your documents to the OnData platform for analysis.  
  
The purpose of this document is to detail the process on how to deploy and configure the Data Analyzer service in your own network environment.  
    
## 1. Configure Docker Host
The Data Analyzer component is packaged as a Docker container image for easy deployment. You need to configure a Docker host machine that is capable of running 
Docker container applications. The Data Analyzer image uses a Linux base image. It can run with Docker Desktop on both Linux and Windows operating systems. For 
details on how to install and configure Docker Desktop, please refer to the [official documentation](https://docs.docker.com/engine/install/) at the Docker 
website. As Docker Desktop is not supported on Windows Server operating systems, the Data Analyzer image may not run on Windows Server operating systems. In future 
OnData may release a version of the Data Analyzer image using a Windows Server base image.  
  
## 2. Obtain Credentials to OnData Container Registry
Follow the steps below to obtain credentials to the OnData container registry.  
a. Login to the OnData Portal and navigate to \`Settings\` -> \`SecureFile\` -> \`Auto Discovery\`  
b. Click on the link \`Configure Data Analyzer\` on the page  
c. Click on the \`Pull Image...\` button, and then click on the \`Generate New Token Password\` button  
d. Make a copy of the \`TOKEN_NAME\` value and the newly generated token password on this page. You need to use them in the steps below  
  
## 3. Deploy Data Analyzer Service
Once you have the credentials to connect to the OnData container registry, you can follow the steps below to deploy the Data Analyzer service from the Data Analyzer 
container image.  
a. Login to OnData's container registry (https://ondataprod.azurecr.us) with account credentials obtained above. You may use the Bash script below:  

\`\`\`bash
TOKEN_NAME = <token name copied above>
TOKEN_PWD = <token password copied above>

echo $TOKEN_PWD | docker login --username $TOKEN_NAME --password-stdin ondataprod.azurecr.us
\`\`\`
  
b. Pull a copy of the OnData Data Analyzer container image from OnData's container registry  

\`\`\`bash
docker pull ondataprod.azurecr.us/products/ondata_data_analyzer:{image tag}
\`\`\`

For example:  
\`\`\`bash
docker pull ondataprod.azurecr.us/products/ondata_data_analyzer:latest
\`\`\`
  
c. Login to the OnData Portal and navigate to \`Settings -> SecureFile -> Auto Discovery\`, and click on \`Configure Data Analyzer\`  
d. Click on \`Registration Code\`, and click on \`Add Registration Code\` to add a new registration code or copy an existing registration code  
e. Assuming you use Docker Swarm as the orchestration engine with Docker secrets, you can run the following command to register the Data Analyzer 
service with the OnData Portal  

PowerShell: 
\`\`\`powershell
# Copy both lines and run at once
@{ content= $(docker run -t --rm -e REGISTRATION_CODE={registration code} ondataprod.azurecr.us/products/ondata_data_analyzer:{image tag}); success= $? } | \`
%{ if ($_.success) { $_.content | docker secret create {secret name} - ; write-host "\`nRegistration succeeded" } else { write-error $_.content }  }
\`\`\`

Bash:
\`\`\`bash
content=$(docker run -t --rm -e REGISTRATION_CODE={registration code} ondataprod.azurecr.us/products/ondata_data_analyzer:{image tag}) && (echo $content 
| docker secret create {secret name} - ; echo -e "Registration succeeded") || echo $content
\`\`\`

Assuming the registration code is GY88-FTT-0EF and the Docker secret name is \`ondata_data_analyzer_secret01\`), below is a sample Bash command:  
\`\`\`bash
content=$(docker run -t --rm -e REGISTRATION_CODE=GY88-FTT-0EF ondataprod.azurecr.us/products/ondata_data_analyzer:latest) && (echo $content 
| docker secret create ondata_data_analyzer_secret01 - ; echo -e "Registration succeeded") || echo $content
\`\`\`

f. If you use a container orchestration engine other than Docker Swarm, you may pipe the registration secret to a file in a secure location, such as 
/securefolder/ondata_data_analyzer_secret01.txt. Below is a sample command:  

\`\`\`bash
content=$(docker run -t --rm -e REGISTRATION_CODE=GY88-FTT-0EF ondataprod.azurecr.us/products/ondata_data_analyzer:latest) && (echo $content | 
tee /securefolder/ondata_data_analyzer_secret01.txt ; echo -e "Registration succeeded") || echo $content
\`\`\`

g. Create a \`docker-compose.yml\` file with the right image and secret name. Again, assuming Docker Swarm is used, below is an example:  

\`\`\`bash
version: '3.4'

services:
  data_analyzer:
    image: ondataprod.azurecr.us/products/ondata_data_analyzer:latest
    ports:
      - "3100:3100"
    secrets:
      - source: ondata_data_analyzer_secret01
        target: /secrets/client_info

secrets:
  ondata_data_analyzer_secret01:
    external: true
\`\`\`

h. Use the \`docker-compose.yml\` file to deploy the Data Analyzer service. Again, assuming Docker Swarm is used, the sample command is listed below:  

\`\`\`bash
docker stack deploy -c docker-compose.yml ondata_data_analyzer
\`\`\`
If you are using a container orchestration engine other than Docker Swarm, you can deploy the Data Analyzer image using the secret generated in Step f above.  

i. Once the Data Analyzer service is up and running, you can access it using \`http://<Host IP Address>:<Port Number>\`, such as http://192.168.1.100:3100 by 
assuming the host IP address 192.168.1.100 and using the sample \`docker-compose.yml\` above  
  
j. Login to the OnData Portal and navigate to \`Settings -> SecureFile -> Auto Discovery\`, and click on \`Configure Data Analyzer\`. Click on the \`Refresh\` button 
and a new entry will show up. Fill in the Data Analyzer service URL \`http://<Host IP Address>:<Port Number>\`, and click on the \`Save Change\` icon to save the change  
  
![Configure Data Analyzer](${ img0 })  
  
k. Click on the \`Activate\` icon on the left side to activate this Data Analyzer service endpoint  
  
l. Navigate to \`Settings -> SecureFile -> Auto Discovery\`, and click on \`Enable Data Analyzer\` to allow all SecureFile clients to interact with the Data Analyzer 
service  
  
## 4. Update Data Analyzer image
You can follow the steps below to update the Data Analyzer image, assuming Docker Swarm is used:  
a. Login to OnData's container registry (https://ondataprod.azurecr.us) with account credentials obtained from \`Step 2. Obtain Credentials to OnData Container Registry\` 
above  
  
b. Pull the updated version of the Data Analyzer container image from OnData  
  
\`\`\`bash
docker pull ondataprod.azurecr.us/products/ondata_data_analyzer:{image tag}
\`\`\`
  
c. Remove the existing deployment of the Data Analyzer application  

\`\`\`bash
docker stack rm ondata_data_analyzer
\`\`\`

d. Update the \`docker-compose.yml\` file with the desired version or image tag, and deploy the Data Analyzer application again   

\`\`\`bash
docker stack deploy -c docker-compose.yml ondata_data_analyzer
\`\`\`
  `
  
export default content;
